import React from 'react';
import { FiSearch } from 'react-icons/fi';
import ViewButton from '../common/ViewButton';

function CompaniesTab({
  companies,
  loading,
  pagination,
  search,
  typeFilter,
  statusFilter,
  onSearchChange,
  onTypeFilterChange,
  onStatusFilterChange,
  onRefresh,
  onPageChange,
  onViewCompany,
}) {
  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'bg-[#e8efe5] text-[#4a6b3f]';
    if (s === 'dissolved' || s === 'struck off') return 'bg-[#f2e9e6] text-[#9a564a]';
    if (s === 'inactive') return 'bg-[#f3ebd7] text-[#8a6a1f]';
    return 'bg-[#f2eee6] text-[#8a7d66]';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#4a6b3f';
    if (score >= 50) return '#8a6a1f';
    return '#9a564a';
  };

  const totalPages = pagination ? Math.ceil((pagination.total || 0) / (pagination.limit || 10)) : 1;
  const currentPage = pagination?.page || 1;

  return (
    <div>
      {/* Page title + Refresh now live in AdminDashboardPage's top header
          (one source of truth — no second-pass label inside the tab). */}

      {/* Search / Filters */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7a7060]" />
            <input
              type="text"
              placeholder="Search companies by name or CIN..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-[13px] outline-none transition-shadow focus:shadow-sm focus:border-[#145f5f40]"
              style={{ borderColor: '#e8e2d6' }}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <select
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value)}
            className="px-4 py-2 border rounded-lg text-[13px] transition-colors hover:border-[#b8b0a0]"
            style={{ borderColor: '#e8e2d6' }}
          >
            <option value="all">All Types</option>
            <option value="Pvt Ltd">Pvt Ltd</option>
            <option value="LLP">LLP</option>
            <option value="Public">Public</option>
            <option value="OPC">OPC</option>
            <option value="Sec 8">Sec 8</option>
            <option value="Foreign Company">Foreign Company</option>
            <option value="Nidhi Company">Nidhi Company</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="px-4 py-2 border rounded-lg text-[13px] transition-colors hover:border-[#b8b0a0]"
            style={{ borderColor: '#e8e2d6' }}
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Dissolved">Dissolved</option>
            <option value="Struck Off">Struck Off</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-[#7a7060]">
          <div className="animate-pulse">Loading companies…</div>
        </div>
      ) : (
      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
        <div className="overflow-x-auto cs-table-scroll">
          <table className="w-full min-w-[900px] text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#7a7060] border-b" style={{ borderColor: '#e8e2d6', background: '#f8f5ef' }}>
                <th className="text-left py-3 px-4">Company</th>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Score</th>
                <th className="text-left py-3 px-4">Created By</th>
                <th className="text-left py-3 px-4">Claimed By</th>
                <th className="text-left py-3 px-4">Registered</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr
                  key={company._id}
                  onClick={() => onViewCompany(company)}
                  className="border-b last:border-0 cursor-pointer transition-colors hover:bg-[#f8f5ef]"
                  style={{ borderColor: '#ddd6c6' }}
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-[#08111f]">{company.name}</div>
                    {company.cin && (
                      <div className="text-[10px] text-[#7a7060] mt-0.5">{company.cin}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#145f5f22', color: '#145f5f' }}>
                      {company.type}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusBadge(company.status)}`}>
                      {company.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-semibold" style={{ color: getScoreColor(company.score) }}>
                      {company.score != null ? company.score : '-'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-[#08111f]">{company.createdBy?.fullName || '-'}</div>
                    {company.createdBy?.email && (
                      <div className="text-[10px] text-[#7a7060]">{company.createdBy.email}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {company.owner ? (
                      <>
                        <div className="text-[#08111f]">{company.owner.fullName || '-'}</div>
                        {company.owner.email && (
                          <div className="text-[10px] text-[#7a7060]">{company.owner.email}</div>
                        )}
                      </>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#f3ebd7] text-[#8a6a1f]">
                        Unclaimed
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-[#7a7060]">
                    {company.createdAt ? new Date(company.createdAt).toLocaleDateString('en-IN') : '-'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <ViewButton onClick={(e) => { e.stopPropagation(); onViewCompany(company); }} title="View company" />
                  </td>
                </tr>
              ))}
              {companies.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-[#7a7060]">
                    No companies found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {companies.length > 0 && pagination && (
        <div className="flex items-center justify-between mt-4 text-[12px] text-[#7a7060]">
          <div>
            Showing {companies.length} of {pagination.total || companies.length} companies
          </div>
          <div className="flex gap-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="px-3 py-1 border rounded transition-colors hover:bg-[#f8f5ef] hover:border-[#b8b0a0] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: '#e8e2d6' }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    className={`px-3 py-1 border rounded transition-colors ${
                      p === currentPage
                        ? 'text-white'
                        : 'hover:bg-[#f8f5ef] hover:border-[#b8b0a0]'
                    }`}
                    style={
                      p === currentPage
                        ? { background: '#145f5f', borderColor: '#145f5f' }
                        : { borderColor: '#e8e2d6' }
                    }
                  >
                    {p}
                  </button>
                )
              )}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="px-3 py-1 border rounded transition-colors hover:bg-[#f8f5ef] hover:border-[#b8b0a0] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: '#e8e2d6' }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompaniesTab;
